import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import LoginIcon from '@mui/icons-material/Login';
import PasswordIcon from '@mui/icons-material/Password';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { useTypedSelector } from 'hooks';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { auth, cleanAuth, toggleEditMode } from 'store';
import { API } from '_helpers';
import CustomInput from '../../../customInput';

const RegistrationForm: React.FC<IProps<IAuth>> = ({ data }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { name, email, password, loading, show, error } = useTypedSelector(
    (state) => state.auth
  );

  const handleClickShowPassword = () => {
    dispatch(auth({ show: !show }));
  };

  React.useEffect(() => {
    return (): void => {
      dispatch(cleanAuth());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    try {
      dispatch(auth({ loading: true, error: '' }));
      const res = await axios.post('/api/register', {
        name,
        email,
        password,
      });
      dispatch(auth({ loading: false, editMode: true }));
      dispatch(
        toggleEditMode({
          identity: true,
          stack: true,
          portfolio: true,
          availability: true,
        })
      );
      router.push('/dev');
    } catch (error: any) {
      return dispatch(
        auth({
          loading: false,
          error: error.response.data.error[0].messages[0].message,
        })
      );
    }
  };

  return (
    <Box sx={{ py: 6 }}>
      <Container>
        <Grid
          container
          spacing={4}
          alignItems="center"
          sx={{
            minHeight: '100vh',
            zIndex: 1,
            position: 'relative',
          }}
        >
          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              <Typography
                component="h2"
                color="text.primary"
                variant="h2"
                gutterBottom
              >
                {data.title}
              </Typography>
              <Typography color="text.secondary" gutterBottom>
                {data.description}
              </Typography>
            </Stack>
            <Stack spacing={2} sx={{ py: 4 }}>
              {error && (
                <Typography color="error" gutterBottom>
                  {error}
                </Typography>
              )}
              <CustomInput
                name="name"
                placeholder={data.form.name}
                icon={<PersonOutlineIcon />}
              />
              <CustomInput
                name="email"
                placeholder={data.form.email}
                icon={<AlternateEmailIcon />}
              />
              <CustomInput
                name="password"
                type={show ? 'text' : 'password'}
                placeholder={data.form.password}
                icon={<PasswordIcon />}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleClickShowPassword}
                    >
                      {show ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <Box>
                <Tooltip
                  title={
                    !name || !email || !password
                      ? router.locale === 'fr'
                        ? 'Tous les champs sont obligatoire'
                        : 'All fileds are required'
                      : ''
                  }
                  arrow
                >
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<LoginIcon />}
                    onClick={!name || !email || !password ? () => {} : onSubmit}
                  >
                    {loading
                      ? router.locale === 'fr'
                        ? 'Chargement...'
                        : 'Loading...'
                      : data.button}
                  </Button>
                </Tooltip>
              </Box>
            </Stack>
            <Typography color="text.secondary">
              {data.text}
              <Link href="/dev/login" passHref>
                <Typography color="secondary" component="a" sx={{ mx: 1 }}>
                  {data.action}
                </Typography>
              </Link>
            </Typography>
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                width: '80%',
                height: '100%',
                m: 'auto',
              }}
            >
              <Image
                alt={data.media.alternativeText}
                src={API + data.media.url}
                width={data.media.width}
                height={data.media.height}
                quality={100}
              />
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default RegistrationForm;
